import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@equine-ui-nx/injection-tokens';
import { Observable, Subject, catchError, of, tap } from 'rxjs';
import { PaymentSummary } from '../interfaces/payment-summary.interface';
import { CartStateService } from './cart-state.service';
import { PaypalClients, PaypalClientSelectorService } from './paypalClientSelector.service';
  enum AdminRoles  {
  ADMIN = 'Admin',
  ACCOUNT_ADMIN = 'AcctAdmin',
  ZONE_ADMIN = 'ZoneAdmin',
  SUPER_ADMIN = 'SuperAdmin',
  }
@Injectable({
  providedIn: 'root'
})
export class PaymentsService {

  private http = inject(HttpClient);
  private apiUrl = inject(API_URL);
  private cartState = inject(CartStateService);
  private paypalClientSelectorService = inject(PaypalClientSelectorService);
  private updateUser$ = new Subject<void>();


  getLoggedInUserId(): string | null {
    const userFromLocalhost = localStorage.getItem('data');
    if (userFromLocalhost) {
      const userData = JSON.parse(userFromLocalhost);
      return userData._id;
    } else {
      return null
    }
  }
  getUserRole(){
    const userFromLocalhost = localStorage.getItem('data');
    if (userFromLocalhost) {
      const userData = JSON.parse(userFromLocalhost);
      return userData?.roles[0]?.name;;
    } else {
      return ""
    }
  }
  isAdminRole(){
    const adminRoles = [
      AdminRoles.ACCOUNT_ADMIN,
      AdminRoles.ADMIN,
      AdminRoles.SUPER_ADMIN,
      AdminRoles.ZONE_ADMIN
    ]
    return adminRoles.includes(this.getUserRole());
  }

  getLoggedInUserDataForDwolla(): {firstName: string, lastName: string, customerId: string, email: string} | null {
    const userFromLocalhost = localStorage.getItem('data');
    if (userFromLocalhost) {
      const userData = JSON.parse(userFromLocalhost);
      return {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        customerId: userData._id
      };
    } else {
      return null
    }
  }


  getPaypalOrderByReference(reference: string): Observable<{id: string, cartId: string}> {
    const PAY_CART_WITH_PAYPAL = `payments/payCartWithPaypal`;

    return this.http.post<{id: string, cartId: string}>(`${this.apiUrl}/${PAY_CART_WITH_PAYPAL}`, {
      reference
    });
  }

  capturePaypalOrder(paypalOrderId: string, cartId: string) {
    const CAPTURE_PAYPAL = `payments/capturePaypalOrder`;

    return this.http.post<{id: string, orders: any[]}>(`${this.apiUrl}/${CAPTURE_PAYPAL}`, { paypalOrderId, cartId });
  }

  createOrderWithProducts(reference: string, purchaseUnits: {productId: string, quantity: number}[], afterPaymentKey: string, additionalReference?: string, intent: 'CAPTURE' | 'AUTHORIZE' = 'CAPTURE') {
    const CREATE_ORDER = `orders/createOrder`;

    const payload: any = {
      reference,
      purchaseUnits: purchaseUnits.filter((each) => each.quantity > 0),
      afterPaymentKey,
      intent
    };

    if (additionalReference) {
      payload['additionalReference'] = additionalReference;
    }

    return this.http.post<{id: string}>(`${this.apiUrl}/${CREATE_ORDER}`, payload);
  }

  getCartSummary(cartId?: string, reference?: string) {
    const CART_SUMMARY = `payments/getPaymentSummary`;

    let payload = {};

    if (cartId) {
      payload = {
        cartId
      }
    }

    if (reference) {
      payload = {
        reference
      }
    }

    // TODO: add interface here
    return this.http.post<any>(`${this.apiUrl}/${CART_SUMMARY}`, payload)
      .pipe(
        tap((response) => {
          this.cartState.setOrders(response.cart.orders);
          this.cartState.setCartId(response.cart.id);
          this.cartState.setCouponAmount(response.cart.coupon);
          this.cartState.setEmptyCartAsFalse();

          const client = response.paypalClient;
          const enableCouponAndDonationSection = client === PaypalClients.IEA;
          this.cartState.setCouponAndDonationSection(enableCouponAndDonationSection);
        }),
        catchError(() => {
          this.cartState.setEmptyCartAsTrue();
          return of(null)
        })
      );
  }

  setCartAsEmptyAfterPayment() {
    this.cartState.setEmptyCartAsTrue();
    this.cartState.setOrders([]);
    this.cartState.setCouponAmount(0);
  }

  addToCart(reference: string, orderIds: string[], payerReference: string) {
    const ADD_TO_CART = `carts/addToCart`;

    const payload = {
      reference,
      orderIds,
      payerReference
    };

    return this.http.post<{id: string}>(`${this.apiUrl}/${ADD_TO_CART}`, payload);
  }

  removeFromCart(reference: string, orderIds: string[]): Observable<any> {
    const REMOVE_FROM_CART = `carts/removeFromCart`;

    const payload = {
      reference,
      orderIds
    };

    return this.http.post(`${this.apiUrl}/${REMOVE_FROM_CART}`, payload);
  }

  addCouponToCart(cartReference: string, customerFacingCoupon: string): Observable<any> {
    const ADD_COUPON_TO_CART = `carts/addCouponToCart`;

    const payload = {
      reference: cartReference,
      customerFacingCoupon
    }

    return this.http.post<any>(`${this.apiUrl}/${ADD_COUPON_TO_CART}`, payload);
  }


  applyCouponToFullfillPayment(cartReference: string): Observable<any> {
    const APPLY_COUPON_TO_FULLFILL_PAYMENT = `payments/applyCouponToFullfillCart`;

    const payload = {
      reference: cartReference,
    }

    return this.http.post<any>(`${this.apiUrl}/${APPLY_COUPON_TO_FULLFILL_PAYMENT}`, payload);
  }

  createDonationOrder(reference: string, additionalReference: string, donationCode: 'D-05' | 'D-10' | 'D-25', afterPaymentKey?: string) {
    const CREATE_ORDER = `orders/createOrder`;

    const Donations = {
      'D-05': 'prod_mNdhEljx0V',
      'D-10': 'prod_xBdFE5FY1M',
      'D-25': 'prod_RebZ77Ivqt',
    }

    const purchaseUnits = [{
      productId: Donations[donationCode],
      quantity: 1
    }];

    const payload: any = {
      reference: reference + '-DONATION',
      purchaseUnits,
      specialSection: 'DONATION',
      additionalReference
    };

    if (afterPaymentKey) {
      payload['afterPaymentKey'] = afterPaymentKey;
    }

    return this.http.post<{id: string}>(`${this.apiUrl}/${CREATE_ORDER}`, payload);
  }

  getPaymentSummary(cartId?: string, cartReference?: string): Observable<PaymentSummary> {
    const GET_PAYMENT_SUMMARY = `payments/getPaymentSummary`
    const params = cartId ? {cartId} : {cartReference};
    return this.http.post<PaymentSummary>(`${this.apiUrl}/${GET_PAYMENT_SUMMARY}`, params);
  }

  getCurrentDonation(cartId?: string, reference?: string) {
    const CURRENT_DONATION = `payments/getCurrentDonation`;

    let payload = {};

    if (cartId) {
      payload = {
        cartId
      }
    }

    if (reference) {
      payload = {
        reference
      }
    }

    return this.http.post<{orderId: string, additionalReference: string}>(`${this.apiUrl}/${CURRENT_DONATION}`, payload);
  }

  needsUserUpdate(): Observable<void> {
    return this.updateUser$.asObservable();
  }

  updateUser() {
    this.updateUser$.next();
  }

  getTotalFromPaymentUnits(payload: any[]): Observable<{total: number}> {
    const GET_TOTAL_FROM_PAYMENT_UNITS = `payments/getTotalFromPurchaseItemsQuery`;

    return this.http.post<{total: number}>(`${this.apiUrl}/${GET_TOTAL_FROM_PAYMENT_UNITS}`, payload);

  }

  assignNonPaidMembership(membershipId: string) {
    const url = `${this.apiUrl}/payments/assignNonPaidMembership`;
    return this.http.post(url, {membershipId});
  }

  getReceiptFromCartId(cartId: string) {
    const GET_RECEIPT_FROM_CART_ID = `payments/getReceiptFromCartId`;

    return this.http.post<any>(`${this.apiUrl}/${GET_RECEIPT_FROM_CART_ID}`, {cartId});
  }

  getRemainingItemsForRefund(orderId: string) {
    const GET_REMAINING_ITEMS_FOR_REFUND = `payments/getRemainingRefundItems`;

    return this.http.post<any>(`${this.apiUrl}/${GET_REMAINING_ITEMS_FOR_REFUND}`, {orderId});
  }

  processRefund(orderId: string, items: {referenceId: string, quantity: number}[],reason: string, note: string, who: string) {
    const PROCESS_REFUND = `payments/refundOrder`;

    return this.http.post<any>(`${this.apiUrl}/${PROCESS_REFUND}`, {
      orderId,
      items,
      reason,
      note,
      who
    });
  }

  getRefundsFromOrderIds(orderIds: string[]) {
    const GET_REFUNDS_BY_ORDER_IDS = `payments/getRefundsByOrderIds`;

    return this.http.post<any>(`${this.apiUrl}/${GET_REFUNDS_BY_ORDER_IDS}`, {orderIds});
  }

  requiresOpenPopup(error: {error?: {message?: string}}): boolean {
    if (error?.error?.message) {
      if (error?.error?.message.includes('SPECIAL_ACTION_REQUIRED_1')) {
        return true;
      }
    }
    return false;
  }

  removeAllItemsFromCart(reference: string): Observable<any> {
    const REMOVE_FROM_CART = `carts/removeFromCart`;

    const payload = {
      reference,
      removeAll: true
    };

    return this.http.post(`${this.apiUrl}/${REMOVE_FROM_CART}`, payload);
  }


  getDwollaLinkFromClientId(clientId: string): Observable<any> {
    const GET_DWOLLA_LINK_FROM_CLIENT_ID = `payments/getDwollaLinkByClientId`;

    return this.http.post(`${this.apiUrl}/${GET_DWOLLA_LINK_FROM_CLIENT_ID}`, {clientId});
  }

  createOrUpdateDwollaLinkWithAcceptance(clientId: string): Observable<any> {
    const CREATE_OR_UPDATE_DWOLLA_LINK_WITH_ACCEPTANCE = `payments/createOrUpdateDwollaLinkWithAcceptance`;

    return this.http.post(`${this.apiUrl}/${CREATE_OR_UPDATE_DWOLLA_LINK_WITH_ACCEPTANCE}`, {clientId});
  }


  createDwollaCustomer(dwollaCustomer: {firstName: string, lastName: string, customerId: string, email: string}, businessName?: string): Observable<any> {
    const CREATE_CUSTOMER = `payments/createDwollaCustomer`;

    const payload: any = {
      reference: dwollaCustomer.customerId,
      firstName: dwollaCustomer.firstName,
      lastName: dwollaCustomer.lastName,
      email: dwollaCustomer.email
    }

    if (businessName) {
      payload['businessName'] = businessName
    }

    return this.http.post(`${this.apiUrl}/${CREATE_CUSTOMER}`, payload);

  }

  createExchangeSession(customerUrl: string): Observable<{redirect: string}> {
    const EXCHANGE_SESSION = `payments/getDwollaExchangeSession`;
    
    return this.http.post<{redirect: string}>(`${this.apiUrl}/${EXCHANGE_SESSION}`, {customerUrl});
  }

  addNewAccountWithExchange(linkId: string, exchangeToken: string, bankAccountType: string, nickName: string) {
    const ADD_ACCOUNT_WITH_EXCHANGE = `payments/addBankAccountWithExchange`;

    const payload = {
      linkId,
      exchangeToken,
      bankAccountType,
      nickName
    }

    return this.http.post(`${this.apiUrl}/${ADD_ACCOUNT_WITH_EXCHANGE}`, payload);
  }

  captureWithAch(cartId: string, dwollaLinkId: string, sourceId: string) {
    const CAPTURE_WITH_ACH = `payments/captureWithAch`;

    const payload = {
      cartId,
      dwollaLinkId,
      sourceId
    };

    return this.http.post(`${this.apiUrl}/${CAPTURE_WITH_ACH}`, payload);

  }
  
  removeAccount(dwollaLinkId: string, sourceId: string) {
    const REMOVE_ACCOUNT = `payments/removeAchAccount`;

    const payload = {
      dwollaLinkId,
      sourceId
    };

    return this.http.post(`${this.apiUrl}/${REMOVE_ACCOUNT}`, payload);

  }

  cloneOrderAsCaptureAndAddToCart(orderId: string, type: string, additionalReference: string, cartReference: string) {
    const CLONE = `payments/cloneOrderAsCaptureAndAddToCart`;

    const payload = {
      orderId,
      type,
      additionalReference,
      cartReference
    };

    return this.http.post(`${this.apiUrl}/${CLONE}`, payload);
  }

  cloneBraintreeAchNotSettledCart(cartId: string) {
    const BT_CLONE = `payments/addOrCreateFailedBraintreeAchCart`;

    return this.http.post(`${this.apiUrl}/${BT_CLONE}`, {cartId});
  }
  
}
